"use client";
import React, { useState, useEffect /* , useCallback */, useMemo } from "react";
import Image from "next/image";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import ExternalLink from "@/app/assets/icons/external-arrow";

import useNavItems from "@/app/hooks/nav-items.hook";
import Mail from "@/app/assets/icons/mail";
import Phone from "@/app/assets/icons/phone";
// import GooglePlay from "@/app/assets/icons/googleplay";
import Close from "@/app/assets/icons/close";
import Menu from "@/app/assets/icons/menu";
import useRequest from "@/app/hooks/request.hook";
import { useRouter /* , useSearchParams */ } from "next/navigation";
import {
    ICM_MAIL,
    ICM_NAME_FULL,
    ICM_PHN_1,
    ICM_PHN_2 /* , ProductType */,
    // routeBasedLogoUrl,
} from "@/app/utils/constants";
import { DirectPaths } from "@/config/routes";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import Avatar from "@/app/assets/icons/avatar";
import { usePathname } from "next/navigation";
// import { initBranch, trackEvent } from "../utils/branchio";
import { useCleverTapEvents, useCleverTapUser } from "../hooks/use-clevertap";
import { /* getReleventDomain, */ getXPartner, setRedirectTo } from "@/app/utils/helper";
import dynamic from "next/dynamic";

const SideDrawer = dynamic(() => import("./profile-drawer"), { ssr: false });
const GetICMAppModal = dynamic(() => import("@/app/components/get-icm-app-modal"), {
    ssr: false,
});
const FdAppDrawer = dynamic(() => import("@/app/alts/fd/components/fd-app-drawer"), {
    ssr: false,
});
import { getActiveNavLogoUrl } from "@/app/utils/get-nav-logo-url";
import HeaderSkeleton from "./loaders/header-skeleton";
import { useAuth } from "@/context/authcontext";
import Link from "next/link";
import useMobile from "../hooks/use-mobile";
import NavigationMenuComponent from "@/components/ui/navigation-menu";
/* import {
    ProductRoute,
    productRoute,
    ProductType,
} from "@/types/product"; */
import { InvestmentOptions /* , SubDomainMap */ } from "@/app/interface/platform";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import { shouldShowPushPromptNow } from "@/app/utils/push-notification-prompt";

export type { NavItem, NavSubItem } from "../../types/nav-item.types";

interface NavbarProps {
    active: string;
    navBarHidden?: boolean;
}

interface UserData {
    email: string;
    userId: string;
    verified: boolean;
    phoneVerified: string;
    phone: string;
    name: string;
    emailVerified: boolean;
    userType: string;
    referrer: string;
    success: boolean;
}

// Define proper types for API request states
interface ApiRequest {
    loading: boolean;
    error: Error | string | unknown | null; // Using 'any' here to allow any error type
}

interface ApiRequests {
    userEmail: ApiRequest;
    // Add more API request states as needed
}

enum RedirectEvent {
    GET_STARTED = "Get_Started",
    LOGIN = "Login",
    SIGNUP = "Signup",
}

interface PlgPopup {
    show: boolean;
    nav: string;
}

const Navbar: React.FC<NavbarProps> = ({ active, navBarHidden }) => {
    const { hasLoaded } = useMobile();
    const router = useRouter();
    const pathname = usePathname();
    // const searchParamS = useSearchParams();
    const { cleverTapTrackEvent } = useCleverTapEvents();
    // Get query params safely
    const searchParams =
        typeof window !== "undefined"
            ? Object.fromEntries(new URLSearchParams(window.location.search))
            : {};

    const isPartner = searchParams?.partner;
    const isXplatform = searchParams?.xPlatform;

    // Determine navbar visibility deterministically
    const shouldShowNav = hasLoaded ? !(isXplatform || isPartner) : true;

    const { isAuthenticated, isAuthFetched } = useAuth();
    const { onUserLogin, isInitialized: isCleverTapInitialized } = useCleverTapUser();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
    const { request } = useRequest(process.env.API_BASE_URL);
    const [userData, setUserData] = useState<UserData>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [apiRequests, setApiRequests] = useState<ApiRequests>({
        userEmail: { loading: true, error: null },
    });
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [isHomePage, setIsHomePage] = useState<boolean>(false);
    // const [isFdPage, setIsFdPage] = useState<boolean>(false);
    const [showAppModal, setShowAppModal] = useState<boolean>(false);
    const [isAppDrawerOpen, setIsAppDrawerOpen] = useState<boolean>(false);

    // Get a new searchParamS string by merging the current
    // searchParamS with a provided key/value pair
    /* const createQueryString = useCallback(
        (name: string, value: string) => {
            const params = new URLSearchParams(searchParamS.toString());
            params.set(name, value);

            return params.toString();
        },
        [searchParamS],
    ); */

    const toggleDownloadApp = () => {
        setShowAppModal(!showAppModal);
        setIsAppDrawerOpen(!isAppDrawerOpen);
    };

    useEffect(() => {
        setIsHomePage(pathname === "/");
    }, [pathname]);

    useEffect(() => {
        // Wait until the auth status resolves before deciding whether to fetch the
        // user profile. This avoids firing authenticated-only requests before we know
        // if there is a valid session.
        if (!isAuthFetched) {
            return;
        }

        // Anonymous visitor: there is no session, so skip the user lookup entirely.
        // Hitting `users/getUserEmail` without a session returns HTTP 401 and pollutes
        // the console with failed-request errors, so we short-circuit here instead.
        if (!isAuthenticated) {
            setUserData(undefined);
            setApiRequests((prev) => ({
                ...prev,
                userEmail: { loading: false, error: null },
            }));
            return;
        }

        // AuthContext is the source of truth for session presence. Mark logged-in
        // immediately so the avatar does not fall back to Login/Signup if the
        // subsequent profile fetch fails or is slow.

        const fetchUserEmail = async () => {
            setApiRequests((prev) => ({
                ...prev,
                userEmail: { loading: true, error: null },
            }));

            try {
                const response = await request({
                    url: "users/getUserEmail",
                    method: "GET",
                    headers: { "X-partner": getXPartner() },
                });

                if (response?.data) {
                    setUserData(response.data);
                }

                setApiRequests((prev) => ({
                    ...prev,
                    userEmail: { loading: false, error: null },
                }));
            } catch (error) {
                // Keep isLoggedIn true — session is still valid; profile drawer
                // can open with empty name/email until a later refresh succeeds.
                setApiRequests((prev) => ({
                    ...prev,
                    userEmail: { loading: false, error },
                }));
            }
        };

        fetchUserEmail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthFetched, isAuthenticated]);

    // Check if any API request is still loading
    useEffect(() => {
        const isAnyApiLoading = Object.values(apiRequests).some((request) => request.loading);
        setIsLoading(isAnyApiLoading);
    }, [
        Object.values(apiRequests)
            .map((req) => req.loading)
            .join(","),
    ]);

    const isPlgPopupNavContext = (): boolean =>
        pathname.includes(`${DirectPaths.UNLISTED_SHARES}/`);

    const handlePlgPopupNavigation = (navUrl: string): boolean => {
        const plgPopup: PlgPopup = JSON.parse(window.sessionStorage.getItem("plgPopup") || "null");
        if (!plgPopup?.show) {
            return false;
        }

        if (isPlgPopupNavContext()) {
            plgPopup.nav = navUrl;
            window.sessionStorage.setItem("plgPopup", JSON.stringify(plgPopup));
            window.history.go(-1);
            return true;
        }

        window.sessionStorage.removeItem("plgPopup");
        return false;
    };

    const handleNavClick = (
        url: string,
        isMobileMenu: boolean,
        eventName?: string,
        branchEvent?: string,
        label?: string,
        isExternal?: boolean,
    ) => {
        const newUrl = handlePrePostUrl(url);

        if (isMobileMenu) {
            setIsMobileMenuOpen(false);
        }

        if (handlePlgPopupNavigation(newUrl)) {
            return;
        }

        const label_list = ["Media", "Learn"];

        if (eventName) {
            setAnalyticsEvent(newUrl, eventName);
        }

        // 🔥 Fire analytics async, but don’t block navigation
        if (branchEvent) {
            (async () => {
                try {
                    // await initBranch();
                    // await trackEvent(
                    //     branchEvent,
                    //     userData?.email || "",
                    //     userData?.phone || "",
                    //     userData?.userId || "",
                    // );
                    cleverTapTrackEvent(branchEvent, {
                        email: userData?.email || "",
                        phone: userData?.phone || "",
                        userId: userData?.userId || "",
                    });
                } catch (error) {
                    console.error("Error initializing Branch:", error);
                }
            })();
        }

        // ✅ Redirect immediately
        if (isExternal || label_list.includes(String(label))) {
            window.open(newUrl, "_blank", "noopener,noreferrer");
        } else {
            window.location.assign(newUrl);
        }
    };

    /**
     *  pre & post login url redirection
     *  default urls are pre login & if isAuthenticated than redirect to post login
     */
    const handlePrePostUrl = (url: string): string => {
        if (isAuthenticated) {
            if (url.includes(DirectPaths.UNLISTED_SHARES)) {
                // update require: for asset detail pages
                return process.env.ICM_UE_POST_URL_LISTING || "";
            }
        }
        return url;
    };

    const navItems = useNavItems({ active, isLoggedIn: Boolean(isAuthenticated) });

    const logoUrl = useMemo(
        () => getActiveNavLogoUrl(navItems, process.env.ICM_LOGO_URL ?? ""),
        [navItems],
    );

    const handleLogin = (event: string) => {
        const isUE = window.location.href.includes(DirectPaths.UNLISTED_SHARES);
        const isBullion = window.location.href.includes(String(DirectPaths.BULLION_POSTFIX_PATH));

        let rediretTo: string = "";

        if (event === RedirectEvent.GET_STARTED || event === RedirectEvent.LOGIN) {
            rediretTo = "/login";
        } else if (event === RedirectEvent.SIGNUP) {
            rediretTo = "/signup";
        }

        // Get query string directly from window.location.search
        const currentQueryString = window.location.search;
        const eventName = `Top_Nav_Bar_${event}`;

        // initBranch()
        //     .then(() => {
        //         trackEvent(eventName, "", "");
        //     })
        //     .catch((error) => console.error("Error initializing Branch:", error));

        setAnalyticsEvent(window.location.origin, eventName);
        // Preserve query parameters when redirecting
        if (isUE) {
            setRedirectTo(
                `${process.env.ICM_UE_POST_URL + "/unlisted-shares"}${currentQueryString}`,
            );
            window.location.href = `${process.env.ICM_UE_POST_URL + "/unlisted-shares"}${rediretTo}${currentQueryString}`;
        } else if (isBullion) {
            setRedirectTo(
                `${process.env.ICM_BULLION_POST_URL + DirectPaths.BULLION_POSTFIX_PATH + DirectPaths.GOLD}${currentQueryString}`,
            );
            window.location.href = `${process.env.ICM_BULLION_POST_URL + DirectPaths.BULLION}${rediretTo}${currentQueryString}`;
        } else {
            router.push(`${rediretTo}${currentQueryString}`);
        }
        return;
    };

    const handleProfileDrawerOpen = () => {
        setIsDrawerOpen(true);

        if (!userData?.userId || !isCleverTapInitialized || !shouldShowPushPromptNow()) {
            return;
        }

        onUserLogin(
            {
                identity: userData.userId,
                name: userData.name,
                email: userData.email,
                phone: userData.phone,
            },
            true,
        );
    };

    const handleLogoClick = (): void => {
        const currentUrl = typeof window !== "undefined" ? window.location.href : "";
        const logoNavUrl = isAuthenticated ? DirectPaths.PLATFORM : DirectPaths.HOME;

        if (handlePlgPopupNavigation(logoNavUrl)) {
            return;
        }

        /* const postUrl = process.env.ICM_UE_POST_URL_LISTING;
        // const preUrl = isUE ? `${process.env.ICM_UE_URL}/login` : process.env.ICM_URL || "/";
        const preUrl = process.env.ICM_URL;

        if (!postUrl || !preUrl) {
            console.error("Environment variables ICM_POST_URL or ICM_URL are not properly defined");
            return;
        } */

        for (const item of navItems) {
            if (item.href.includes(currentUrl) && !item.subItems?.length) {
            }
            if (item.href === currentUrl && !item.subItems?.length) {
                window.location.href = item.href ?? "";
            }

            const matchedSubItem = item.subItems?.find((sub) => currentUrl.includes(sub.href));

            if (matchedSubItem) {
                window.location.href = matchedSubItem.href ?? item.href;
            }
        }
    };

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isMobileMenuOpen]);

    // Wait for auth to resolve before painting Login/Signup vs avatar so we
    // never flash the wrong CTA while /authStatus (or a route revalidation) is in flight.
    if ((isLoading || !isAuthFetched) && shouldShowNav) {
        return <HeaderSkeleton shouldShowNav={shouldShowNav} />;
    }

    return (
        <div
            id="navbar"
            className={`${shouldShowNav ? "flex" : "hidden"} sticky top-0 z-50 h-[68px] items-center justify-center bg-white md:h-24`}
        >
            <div className="lg-[px-0] relative flex w-full gap-s6 px-4 sm:container md:px-0">
                {!navBarHidden && (
                    <div className="flex items-center sm:hidden">
                        <button
                            type="button"
                            onClick={() => setIsMobileMenuOpen((prevState) => !prevState)}
                            aria-controls="mobile-menu"
                            aria-expanded={isMobileMenuOpen}
                        >
                            <span className="sr-only">
                                {isMobileMenuOpen ? "Close menu" : "Open menu"}
                            </span>
                            <div className="flex h-5 w-6 flex-col items-center justify-between">
                                {isMobileMenuOpen ? <Close /> : <Menu />}
                            </div>
                        </button>
                    </div>
                )}
                <div className="flex w-full items-center justify-between py-s8 md:mx-s10">
                    <div className="flex flex-1 gap-20">
                        <div className="animate-fade" onClick={() => handleLogoClick()}>
                            <Image
                                src={logoUrl}
                                alt={ICM_NAME_FULL}
                                height={100}
                                width={100}
                                className="h-auto w-[90px] cursor-pointer md:h-auto md:w-[100px]"
                            />
                        </div>

                        {!navBarHidden && (
                            <div className="hidden flex-1 gap-s14 self-center sm:flex">
                                {navItems
                                    .filter(
                                        (item) =>
                                            item.label !== InvestmentOptions.Portfolio ||
                                            (isAuthenticated && !isHomePage),
                                    )
                                    .map((item) => {
                                        if (item.label === "") {
                                            return;
                                        }

                                        if (item.subItems && item.subItems.length > 0) {
                                            return (
                                                <NavigationMenuComponent
                                                    listItem={item}
                                                    active={Boolean(item.isActive)}
                                                    key={item.label}
                                                />
                                            );
                                        }
                                        return (
                                            <Link
                                                href={item.href}
                                                key={item.label}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleNavClick(
                                                        item.href,
                                                        false,
                                                        item.eventName,
                                                        item.branchEvent,
                                                        item.label,
                                                        item.isExternal,
                                                    );
                                                }}
                                                target={
                                                    item.isExternal ? "_blank" : undefined
                                                }
                                                rel={
                                                    item.isExternal
                                                        ? "noopener noreferrer"
                                                        : undefined
                                                }
                                            >
                                                <div
                                                    className={`${item?.isNew ? "" : ""} animate-fade relative flex cursor-pointer items-center text-sm font-medium hover:text-gray-900 ${
                                                        item.isActive
                                                            ? "text-slate-900"
                                                            : "text-slate-500"
                                                    }`}
                                                    aria-current={
                                                        item.isActive ? "page" : undefined
                                                    }
                                                >
                                                    <div className="text-md font-semibold">
                                                        {item.label}
                                                    </div>
                                                    {item.isNew && (
                                                        <span className="absolute -right-[13px] bottom-[16.5px] animate-pulse text-[10px] font-semibold text-brand-primary-500">
                                                            NEW
                                                        </span>
                                                    )}
                                                    {/* {item.isExternal && (
                                                        <span className="p3">
                                                            <div className="flex items-center gap-1">
                                                                <ExternalLink size={3} />
                                                            </div>
                                                        </span>
                                                    )} */}
                                                </div>
                                            </Link>
                                        );
                                    })}
                            </div>
                        )}
                    </div>
                    {isDrawerOpen && (
                        <SideDrawer
                            isOpen={isDrawerOpen}
                            onClose={() => setIsDrawerOpen(false)}
                            position="right"
                            name={userData?.name}
                            email={userData?.email}
                            userId={userData?.userId}
                            phone={userData?.phone}
                        />
                    )}
                    {!navBarHidden &&
                        (isAuthenticated && !isHomePage ? (
                            <div onClick={handleProfileDrawerOpen}>
                                <Avatar className="cursor-pointer" />
                            </div>
                        ) : (
                            <div className="animate-fade flex items-center gap-s5">
                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.SecondaryDefault}
                                    className="visible mr-s9 bg-white md:hidden"
                                    label="Get started"
                                    onClick={() => handleLogin("Get_Started")}
                                />

                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.SecondaryDefault}
                                    className="hidden bg-white md:block"
                                    label="Log in"
                                    onClick={() => handleLogin("Login")}
                                />

                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.SecondaryDefault}
                                    className="hidden bg-white md:block"
                                    label="Sign up"
                                    onClick={() => handleLogin(RedirectEvent.SIGNUP)}
                                />
                            </div>
                        ))}
                </div>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <div
                        className="bg-black/50 fixed inset-0 z-50"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        <div
                            className="absolute inset-y-0 left-0 flex w-full flex-col border border-black bg-white"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between border-b p-4">
                                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2">
                                    <Close />
                                </button>
                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.SecondaryDefault}
                                    className="bg-white"
                                    label="Get started"
                                    onClick={() => handleLogin("Get_Started")}
                                />
                            </div>

                            <div className="h-[calc(100vh-70px)] overflow-y-auto">
                                <ul className="px-6 py-4">
                                    {navItems.map((item, index) => {
                                        if (item.label === "") {
                                            return;
                                        }

                                        if (item.subItems && item.subItems.length > 0) {
                                            const id =
                                                item.label?.toLowerCase().replace(/\s+/g, "-") +
                                                "-" +
                                                index;

                                            return (
                                                <Accordion
                                                    key={id}
                                                    id={id}
                                                    type="single"
                                                    collapsible
                                                    className={`mx-0 mb-3 w-full md:mx-auto md:w-full`}
                                                >
                                                    <AccordionItem
                                                        id={"section-item-" + index}
                                                        value={`question-${index}`}
                                                        className={`w-full !border-none`}
                                                    >
                                                        <AccordionTrigger
                                                            id={"section-head-" + index}
                                                            className="w-full border-0 border-dashed border-neutralOpacityDark-20 py-0 [&>svg]:mb-3"
                                                        >
                                                            <p
                                                                className={`mb-3 flex items-center text-base font-medium ${
                                                                    item.isActive
                                                                        ? "text-slate-900"
                                                                        : "text-slate-500"
                                                                }`}
                                                            >
                                                                <span className="mr-4">
                                                                    {item.icon}
                                                                </span>
                                                                {item.label}
                                                                {item.isExternal && (
                                                                    <ExternalLink
                                                                        size={3}
                                                                        className="ml-2"
                                                                    />
                                                                )}
                                                                {item.isNew && (
                                                                    <span className="relative bottom-3 right-2 animate-pulse text-xs font-semibold text-brand-primary-500">
                                                                        NEW
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </AccordionTrigger>
                                                        <AccordionContent
                                                            id={"section-child-" + index}
                                                            className="flex flex-col"
                                                        >
                                                            {item.subItems.map((subItem, ind) => {
                                                                const id =
                                                                    subItem.label
                                                                        ?.toLowerCase()
                                                                        .replace(/\s+/g, "-") +
                                                                    "-" +
                                                                    ind;

                                                                return (
                                                                    <Link
                                                                        href={subItem.href}
                                                                        className={`flex min-w-full items-center border-b border-neutralOpacityDark-10 py-3 pr-4 font-medium ${
                                                                            subItem.isActive
                                                                                ? "text-slate-900"
                                                                                : "text-slate-500"
                                                                        }`}
                                                                        key={id}
                                                                    >
                                                                        <span className="ml-0.5 mr-5 block h-5 w-5">
                                                                            {subItem.icon}
                                                                        </span>
                                                                        {subItem.label}
                                                                    </Link>
                                                                );
                                                            })}
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                </Accordion>
                                            );
                                        }

                                        return (
                                            <li key={item.label} className="mb-6">
                                                <p
                                                    // href={}
                                                    className={`flex items-center text-base font-medium ${
                                                        item.isActive
                                                            ? "text-slate-900"
                                                            : "text-slate-500"
                                                    }`}
                                                    onClick={() =>
                                                        handleNavClick(
                                                            item.href,
                                                            true,
                                                            item.eventName,
                                                            item.branchEvent,
                                                            item.label,
                                                            item.isExternal,
                                                        )
                                                    }
                                                >
                                                    <span className="mr-4">{item.icon}</span>
                                                    {item.label}
                                                    {/* {item.isExternal && (
                                                        <ExternalLink size={3} className="ml-2" />
                                                    )} */}
                                                    {item.isNew && (
                                                        <span className="relative bottom-3 right-2 animate-pulse text-xs font-semibold text-brand-primary-500">
                                                            NEW
                                                        </span>
                                                    )}
                                                </p>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="border-t border-dashed px-6 py-6">
                                    <h3 className="mb-4 text-sm font-semibold text-neutral-400">
                                        HELP & SUPPORT
                                    </h3>
                                    <a
                                        href={`mailto:${ICM_MAIL}`}
                                        className="mb-4 flex items-center text-slate-600"
                                    >
                                        <span className="mr-4">
                                            <Mail />
                                        </span>
                                        {ICM_MAIL}
                                    </a>
                                    <div className="flex items-start text-slate-600">
                                        <span className="mr-4 mt-1">
                                            <Phone />
                                        </span>
                                        <address className="not-italic leading-relaxed">
                                            <a href={`tel:${ICM_PHN_1}`}>{ICM_PHN_1}</a>
                                            <br />
                                            <a href={`tel:${ICM_PHN_2}`}>{ICM_PHN_2}</a>
                                        </address>
                                    </div>
                                </div>

                                <div className="border-t border-dashed px-6 py-6">
                                    {/* <h3 className="mb-4 text-sm font-semibold text-neutral-400">
                                        GET THE MOBILE APP
                                    </h3>
                                    <div className="flex items-center gap-4">
                                        <RegularButton
                                            showRightIcon={false}
                                            size={ButtonSize.Small}
                                            variant={ButtonVariant.SecondaryDefault}
                                            className="max-w-1/2 flex-1 bg-white"
                                            onClick={() =>
                                                window.open(process.env.ICM_APPLE_APP_URL)
                                            }
                                            label="iOS"
                                        />
                                        <RegularButton
                                            showRightIcon={false}
                                            icon={<GooglePlay />}
                                            size={ButtonSize.Small}
                                            variant={ButtonVariant.SecondaryDefault}
                                            className="max-w-1/2 flex-1 bg-white"
                                            onClick={() =>
                                                window.open(process.env.ICM_PLAYSTORE_URL)
                                            }
                                            label="Android"
                                        />
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {showAppModal && <GetICMAppModal open={showAppModal} onClose={toggleDownloadApp} />}
            {isAppDrawerOpen && (
                <FdAppDrawer isOpen={isAppDrawerOpen} onClose={toggleDownloadApp} />
            )}
        </div>
    );
};

export default Navbar;
